import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  ChevronRightIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../../components/animations/FadeIn';
import { polygonApi, PortfolioSite, PaginatedSitesResponse, GenerateFromVaultProgress, fetchV32PortfolioCostSummary, type V32PortfolioCostSummary } from '../../api/polygon';
import { concentrationApi } from '../../api/concentration';
import type { HybridRankedSite } from '@/types';


// Tier styles - monochrome
const TIER_STYLES: Record<number, { bg: string; text: string; label: string }> = {
  1: { bg: 'bg-mono-200', text: 'text-mono-950', label: 'Critical' },
  2: { bg: 'bg-mono-300', text: 'text-mono-950', label: 'High' },
  3: { bg: 'bg-mono-400', text: 'text-mono-950', label: 'Moderate' },
  4: { bg: 'bg-mono-500', text: 'text-mono-950', label: 'Low' },
  5: { bg: 'bg-mono-600', text: 'text-mono-100', label: 'Minimal' },
};

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return `$${(value / 1000).toFixed(0)}K`;
}

export function PolygonSites() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<'escalation' | 'tier' | 'cost' | 'name' | 'priority'>('escalation');
  const [filterTier, setFilterTier] = useState<number | null>(
    searchParams.get('tier') ? parseInt(searchParams.get('tier')!) : null
  );
  const [sites, setSites] = useState<PortfolioSite[]>([]);
  const [hybridScores, setHybridScores] = useState<Record<string, HybridRankedSite>>({});
  const [v32CostBySite, setV32CostBySite] = useState<Record<string, V32PortfolioCostSummary['sites'][number]>>({});

  useEffect(() => {
    let cancelled = false;
    fetchV32PortfolioCostSummary()
      .then(resp => {
        if (cancelled) return;
        const byId: Record<string, V32PortfolioCostSummary['sites'][number]> = {};
        for (const s of resp.sites) byId[s.site_id] = s;
        setV32CostBySite(byId);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[POLYGON] v3.2 portfolio cost summary fetch failed:', err);
      });
    return () => { cancelled = true; };
  }, []);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch sites from API (paginated + server-side search) + hybrid scores
  useEffect(() => {
    async function fetchSites() {
      try {
        setLoading(true);
        const [data, hybrid] = await Promise.all([
          polygonApi.getSites(page, pageSize, debouncedSearch || undefined),
          concentrationApi.getHybridRanking().catch(() => null),
        ]);
        setSites(data.sites || []);
        setTotalCount(data.total_count || 0);
        if (hybrid?.sites) {
          const lookup: Record<string, HybridRankedSite> = {};
          for (const s of hybrid.sites) {
            lookup[s.site_id] = s;
          }
          setHybridScores(lookup);
        }
      } catch (err) {
        console.error('[PolygonSites] Failed to fetch sites:', err);
        setError('Failed to load sites');
      } finally {
        setLoading(false);
      }
    }

    fetchSites();
  }, [page, pageSize, debouncedSearch]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const getTierStyle = (tier: number) => {
    return TIER_STYLES[tier] || TIER_STYLES[3];
  };

  const getUrgency = (tier: number): { label: string; style: string } => {
    const urgencyMap: Record<number, { label: string; style: string }> = {
      1: { label: 'Act Now', style: 'bg-mono-200 text-mono-950' },
      2: { label: '3 months', style: 'bg-mono-300 text-mono-950' },
      3: { label: '6 months', style: 'bg-mono-400 text-mono-950' },
      4: { label: '12 months', style: 'bg-mono-500 text-mono-950' },
      5: { label: 'Monitor', style: 'bg-mono-700 text-mono-200' },
    };
    return urgencyMap[tier] || urgencyMap[3];
  };

  // Filter by tier (client-side) and sort. Search is handled server-side.
  const filteredSites = useMemo(() => {
    const filtered = sites.filter((site) => {
      if (filterTier !== null && site.threat_tier !== filterTier) return false;
      return true;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          return (hybridScores[b.site_id]?.hybrid_score ?? 0) - (hybridScores[a.site_id]?.hybrid_score ?? 0);
        case 'escalation':
          return b.monthly_burn_rate - a.monthly_burn_rate;
        case 'tier':
          return a.threat_tier - b.threat_tier;
        case 'cost':
          return b.current_cost - a.current_cost;
        case 'name':
          return a.site_name.localeCompare(b.site_name);
        default:
          return 0;
      }
    });
  }, [sites, filterTier, sortBy, hybridScores]);

  const handleFilterChange = useCallback((tier: number | null) => {
    setFilterTier(tier);
    if (tier === null) {
      searchParams.delete('tier');
    } else {
      searchParams.set('tier', tier.toString());
    }
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <ArrowPathIcon className="h-8 w-8 text-mono-500 animate-spin mx-auto mb-4" />
          <p className="text-mono-500 text-sm tracking-wider">LOADING PORTFOLIO...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (sites.length === 0) {
    return (
      <div className="space-y-6">
        <FadeIn>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">SITE PORTFOLIO</h1>
              <p className="text-mono-500 text-sm mt-1">
                Environmental liability analysis
              </p>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={200}>
          <div className="flex flex-col items-center justify-center py-20 bg-mono-900 border border-mono-800 rounded-lg">
            <CloudArrowUpIcon className="h-16 w-16 text-mono-600 mb-4" />
            <h2 className="text-xl font-semibold text-mono-200 tracking-wider mb-2 font-orbitron">NO SITES UPLOADED</h2>
            <p className="text-mono-500 text-sm text-center max-w-md mb-6">
              Upload your environmental site portfolio to begin cost-of-delay analysis.
              POLYGON will analyze contamination plume trajectories and predict remediation cost escalation.
            </p>
            <Link
              to="/polygon/upload"
              className="flex items-center px-6 py-3 bg-mono-200 hover:bg-mono-100 text-mono-900 text-xs tracking-widest transition-colors"
            >
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              UPLOAD SITE
            </Link>
          </div>
        </FadeIn>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-mono-100 tracking-wider font-orbitron">SITE PORTFOLIO</h1>
            <p className="text-mono-500 text-sm mt-1">
              {filteredSites.length} sites {filterTier !== null && `in Tier ${filterTier}`}
            </p>
          </div>
          <Link
            to="/polygon/upload"
            className="flex items-center px-4 py-2 bg-mono-200 hover:bg-mono-100 text-mono-900 text-xs tracking-widest transition-colors"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
            UPLOAD SITE
          </Link>
        </div>
      </FadeIn>

      {/* Filters */}
      <FadeIn delay={100}>
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mono-500" />
            <input
              type="text"
              placeholder="Search sites..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-mono-900 border border-mono-800 rounded text-mono-200 text-sm placeholder-mono-600 focus:outline-none focus:border-mono-600"
            />
          </div>

          {/* Tier Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-mono-500" />
            <div className="flex gap-1">
              <button
                onClick={() => handleFilterChange(null)}
                className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
                  filterTier === null
                    ? 'bg-mono-200 text-mono-900'
                    : 'bg-mono-800 text-mono-400 hover:text-mono-200'
                }`}
              >
                ALL
              </button>
              {[1, 2, 3, 4, 5].map((tier) => {
                const style = getTierStyle(tier);
                return (
                  <button
                    key={tier}
                    onClick={() => handleFilterChange(tier)}
                    className={`px-3 py-1.5 text-xs tracking-wider transition-colors ${
                      filterTier === tier
                        ? `${style.bg} ${style.text}`
                        : 'bg-mono-800 text-mono-400 hover:text-mono-200'
                    }`}
                  >
                    T{tier}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowsUpDownIcon className="h-4 w-4 text-mono-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="bg-mono-900 border border-mono-800 rounded px-3 py-1.5 text-mono-200 text-xs tracking-wider focus:outline-none focus:border-mono-600"
            >
              <option value="priority">PRIORITY SCORE</option>
              <option value="escalation">ESCALATION RATE</option>
              <option value="tier">THREAT TIER</option>
              <option value="cost">CURRENT COST</option>
              <option value="name">NAME</option>
            </select>
          </div>
        </div>
      </FadeIn>

      {/* Sites Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredSites.map((site) => {
          const tierStyle = getTierStyle(site.threat_tier);
          const urgency = getUrgency(site.threat_tier);
          const hybrid = hybridScores[site.site_id];
          const prioColor = hybrid
            ? hybrid.priority_category === 'critical' ? '#ef4444'
            : hybrid.priority_category === 'high' ? '#f97316'
            : hybrid.priority_category === 'moderate' ? '#eab308'
            : '#22c55e'
            : undefined;

          return (
            <Link
              key={site.site_id}
              to={`/polygon/sites/${site.site_id}`}
              className="block bg-mono-900 border border-mono-800 hover:border-mono-600 rounded-lg p-5 transition-all hover:bg-mono-850"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold ${tierStyle.bg} ${tierStyle.text}`}>
                    {site.threat_tier}
                  </span>
                  <div>
                    <h3 className="text-mono-100 font-semibold">{site.site_name}</h3>
                    <p className="text-mono-500 text-xs flex items-center gap-1">
                      <MapPinIcon className="h-3 w-3" />
                      {site.state}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hybrid && (
                    <span
                      className="text-xs px-2 py-1 rounded-full font-bold"
                      style={{ backgroundColor: prioColor + '33', color: prioColor }}
                    >
                      {hybrid.hybrid_score.toFixed(0)}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded-full ${urgency.style}`}>
                    {urgency.label}
                  </span>
                </div>
              </div>

              {/* Cost escalation warning for high tier sites */}
              {site.threat_tier <= 2 && (
                <div className="flex items-center gap-2 mb-4 p-2 rounded bg-mono-800">
                  <ExclamationTriangleIcon className="h-4 w-4 text-mono-300" />
                  <span className="text-xs text-mono-300">
                    {site.threat_tier === 1 ? 'Immediate action required' : 'High priority remediation'}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">CURRENT COST</p>
                  <p className="text-mono-100 font-semibold">{formatCurrency(site.current_cost)}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">6 MO COST</p>
                  <p className="text-mono-300 font-semibold">{formatCurrency(site.cost_6mo)}</p>
                  {site.current_cost > 0 && (
                    <p className="text-mono-400 text-xs">+{(((site.cost_6mo - site.current_cost) / site.current_cost) * 100).toFixed(0)}%</p>
                  )}
                </div>
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">
                    COST OF DELAY ({v32CostBySite[site.id]?.delay_horizon_label ?? '5YR'})
                  </p>
                  <p className="text-amber-300 font-semibold">
                    +{formatCurrency(v32CostBySite[site.id]?.cost_of_delay ?? site.monthly_burn_rate * 60)}
                  </p>
                  {v32CostBySite[site.id] && (
                    <p className="text-mono-500 text-[9px] tracking-wider">{v32CostBySite[site.id].dominant_group}-dominant</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-mono-800">
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">PLUME AREA</p>
                  <p className="text-mono-300 text-sm">{(site.plume_area_sqft / 1000).toFixed(1)}K sqft</p>
                </div>
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">CONTAMINANT</p>
                  <p className="text-mono-300 text-sm">{(site.contaminant_type || '').toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">RECOMMENDED</p>
                  <p className="text-mono-300 text-sm">{(() => { const n = site.sentinel_count || site.wells_breached; return `${n} new well${n !== 1 ? 's' : ''}`; })()}</p>
                </div>
                <div>
                  <p className="text-mono-600 text-[10px] tracking-wider mb-1">EXCEED MCL</p>
                  <p className="text-mono-300 text-sm">{site.wells_exceeding_mcl || 0}/{site.wells_total}</p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-mono-800 text-mono-400 text-xs tracking-wider">
                <span
                  onClick={(e) => { e.preventDefault(); navigate(`/polygon/sites/${site.site_id}/cost-analysis`); }}
                  className="hover:text-cyan-400 transition-colors cursor-pointer"
                >
                  COST ANALYSIS
                </span>
                <span className="flex items-center">
                  VIEW SITE
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </span>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Empty state when filtering produces no results */}
      {filteredSites.length === 0 && sites.length > 0 && (
        <FadeIn delay={200}>
          <div className="text-center py-12 text-mono-500">
            No sites found matching your criteria.
          </div>
        </FadeIn>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <FadeIn delay={200}>
          <div className="flex items-center justify-between pt-4">
            <p className="text-mono-500 text-xs tracking-wider">
              Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount} sites
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-xs tracking-wider bg-mono-800 text-mono-400 hover:text-mono-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                PREV
              </button>
              <span className="text-mono-400 text-xs tracking-wider px-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-xs tracking-wider bg-mono-800 text-mono-400 hover:text-mono-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                NEXT
              </button>
            </div>
          </div>
        </FadeIn>
      )}
    </div>
  );
}
